import { CommonModule } from '@angular/common';
import {
	Component,
	input,
	output,
	signal,
	ElementRef,
	ViewChild,
	AfterViewInit,
	OnDestroy,
	computed
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import {
	PrismIconButtonComponent,
	PrismSearchBarComponent,
	PrismButtonComponent,
	PrismIconComponent,
	PrismMenuComponent,
	PrismMenuItemComponent,
	PrismProgressIndicatorComponent,
	PrismOverlayContainerComponent
} from '@shure/prism-angular-components';
import { PrismMenuCustomEvent } from '@shure/prism-web-components';

const PRISM_SPACING_THEME_XM = 12;

export type LoadingProgress = {
	state: 'WaitingToStart' | 'Indeterminate' | 'Determinate' | 'Completed';
	percentComplete: number;
};

@Component({
	selector: 'sh-table-header',
	templateUrl: './table-header.component.html',
	styleUrls: ['./table-header.component.scss'],
	imports: [
		CommonModule,
		TranslocoModule,
		PrismButtonComponent,
		PrismIconComponent,
		PrismIconButtonComponent,
		PrismMenuComponent,
		PrismMenuItemComponent,
		PrismSearchBarComponent,
		PrismProgressIndicatorComponent,
		PrismOverlayContainerComponent
	]
})
export class TableHeaderComponent implements AfterViewInit, OnDestroy {
	private resizeObserver?: ResizeObserver;

	// Used to decide when to collapse slots 2 and 3 into mobile menu based on available width
	@ViewChild('mainControls', { read: ElementRef }) private mainControlsRef?: ElementRef<HTMLElement>;
	// Used to decide the width of the mobile menu overlay container so as to not exceed the table's width
	@ViewChild('tableHeader', { read: ElementRef }) private tableHeaderRef?: ElementRef<HTMLElement>;

	public rowsCount = input<number>();
	public showSearchBar = input<boolean>(true);
	public showExportButton = input<boolean>(true);
	public loadingProgress = input<LoadingProgress>();

	// Emits the current search text value for parent components.
	public searchTextChange = output<string>();

	// Emits export requests with file name and type for parent components.
	public exportSelected = output<{ type: 'csv' | 'xlsx' }>();

	public collapseSlots2And3IntoMenu = signal(false);
	private collapseSlots2And3Width = 0;
	public showMobileMenu = signal(false);
	public mobileMenuWidth = computed(() => {
		const paddingWidth = PRISM_SPACING_THEME_XM;
		return this.tableHeaderWidth() - paddingWidth * 2;
	});
	public tableHeaderWidth = signal<number>(0);

	public onToggleMobileMenu(): void {
		this.showMobileMenu.set(!this.showMobileMenu());
	}

	public onSearch(filterValue: string): void {
		this.searchTextChange.emit(filterValue);
	}

	public onExport(event: PrismMenuCustomEvent<string>): void {
		const type = <'csv' | 'xlsx'>event.target.value;
		this.exportSelected.emit({ type });
	}

	public ngAfterViewInit(): void {
		this.setupResizeObserver();
	}

	public ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
	}

	private setupResizeObserver(): void {
		if (!this.mainControlsRef || !this.tableHeaderRef) return;

		this.resizeObserver = new ResizeObserver(() => {
			this.checkOverflow();
			this.updateTableHeaderWidth();
		});

		this.resizeObserver.observe(this.mainControlsRef.nativeElement);
		this.resizeObserver.observe(this.tableHeaderRef.nativeElement);
	}

	private checkOverflow(): void {
		const container = this.mainControlsRef?.nativeElement;
		if (!container) return;

		const containerWidth = container.offsetWidth;
		let contentWidth = 0;

		// Measure all children to determine total content width
		Array.from(container.children).forEach((child) => {
			contentWidth += (<HTMLElement>child).offsetWidth;
		});

		// Add gap spacing between elements
		const gap = PRISM_SPACING_THEME_XM;
		const totalGapSize = (container.children.length - 1) * gap;
		contentWidth += totalGapSize;

		// Determine which layout mode based on overflow
		const overflow = contentWidth - containerWidth;

		if (overflow >= 0 && !this.collapseSlots2And3IntoMenu()) {
			this.collapseSlots2And3IntoMenu.set(true);
			this.collapseSlots2And3Width = containerWidth;
		}
		if (containerWidth > this.collapseSlots2And3Width && this.collapseSlots2And3IntoMenu()) {
			// Expand slots 2 and 3 out of menu
			this.collapseSlots2And3IntoMenu.set(false);
			// Close mobile menu when all items are expanded back to main controls
			this.showMobileMenu.set(false);
		}
	}

	private updateTableHeaderWidth(): void {
		const headerWidth = this.tableHeaderRef?.nativeElement.offsetWidth;
		if (headerWidth) {
			this.tableHeaderWidth.set(headerWidth);
		}
	}
}
